<script setup lang="ts">
import { computed } from 'vue'
import SiteEditableText from '../../SiteEditableText.vue'
import type { ReviewsBlock } from '../../types'

const props = defineProps<{ block: ReviewsBlock }>()
const featuredReview = computed(() => props.block.content.items[0])
const supportingReviews = computed(() => props.block.content.items.slice(1))
</script>

<template>
  <section :id="block.settings.anchorId" class="site:bg-(--site-color-background) site:py-16 site:@min-[40rem]:py-24">
    <div class="site-site-container site:grid site:gap-10 site:@min-[64rem]:grid-cols-[0.72fr_1.28fr] site:@min-[64rem]:gap-16">
      <div>
        <SiteEditableText v-if="block.content.eyebrow" v-model="block.content.eyebrow" as="p" class="site:text-sm site:font-semibold site:text-(--site-color-primary)" />
        <SiteEditableText v-model="block.content.heading" as="h2" class="site-heading site:mt-3 site:text-3xl site:font-bold site:tracking-tight site:@min-[40rem]:text-5xl" />
        <SiteEditableText v-if="block.content.description" v-model="block.content.description" as="p" class="site:mt-5 site:text-lg site:leading-8 site:text-(--site-color-muted-text)" />
      </div>

      <div class="site:grid site:gap-4">
        <figure v-if="featuredReview" class="site:bg-(--site-color-primary) site:p-7 site:text-(--site-color-primary-text) site:shadow-(--site-shadow-md) site:@min-[40rem]:p-10" :style="{ borderRadius: 'var(--site-radius-lg)' }">
          <div v-if="featuredReview.rating" class="site:text-sm site:tracking-[0.18em]">
            <span role="img" :aria-label="`${featuredReview.rating} out of 5 stars`">{{ '★'.repeat(featuredReview.rating) }}</span>
          </div>
          <SiteEditableText v-model="featuredReview.quote" as="blockquote" class="site-heading site:mt-6 site:text-2xl site:font-semibold site:leading-9 site:@min-[40rem]:text-3xl site:@min-[40rem]:leading-10" />
          <figcaption class="site:mt-8 site:flex site:flex-wrap site:gap-x-2 site:text-sm">
            <SiteEditableText v-model="featuredReview.author" as="span" class="site:font-bold" />
            <SiteEditableText v-if="featuredReview.role" v-model="featuredReview.role" as="span" class="site:opacity-90" />
          </figcaption>
        </figure>

        <div v-if="supportingReviews.length" class="site:grid site:gap-4 site:@min-[48rem]:grid-cols-2">
          <figure v-for="review in supportingReviews" :key="review.id" class="site:border site:border-(--site-color-border) site:bg-(--site-color-surface) site:p-6" :style="{ borderRadius: 'var(--site-radius-md)' }">
            <SiteEditableText v-model="review.quote" as="blockquote" class="site:leading-7" />
            <figcaption class="site:mt-5 site:text-sm">
              <SiteEditableText v-model="review.author" as="span" class="site:font-bold" />
              <SiteEditableText v-if="review.role" v-model="review.role" as="span" class="site:ml-2 site:text-(--site-color-muted-text)" />
            </figcaption>
          </figure>
        </div>
      </div>
    </div>
  </section>
</template>
